// node-browser.js
// Browser-only libp2p node factory. Meaningfully different from
// node.js's Node version, not just a trimmed copy:
//
//   - WebSocket transport only for direct dialing. No TCP (@libp2p/tcp
//     uses Node's `net` module, doesn't exist in a browser at all) — a
//     browser peer can only ever directly reach peers listening on
//     ws://, which is why node.js's WebSocket listener exists on the
//     Node side.
//   - No raw listen addresses. Browsers cannot accept inbound TCP/WS
//     connections under any circumstance — that part is still true.
//     BUT: WebRTC (below) changes what "reachable" means for a browser
//     peer, so this is no longer "dial-only, full stop" the way it used
//     to be.
//   - No mDNS. Browsers have no access to raw multicast/UDP; LAN
//     discovery simply isn't available.
//   - DHT/bootstrap/circuit-relay support mirrors node.js's opt-in
//     design, and matters MORE here than on the Node side: without a
//     bootstrap peer to connect through, a browser peer has no way to
//     discover anyone at all — it would need to be handed a specific
//     peer's address out-of-band for every single connection. See
//     src/bootstrap-peer.js.
//
// Browser-to-browser (WebRTC): two browser peers are both dial-only in
// the raw-transport sense — neither can accept a direct TCP/WS
// connection — so they can't reach each other with the transports
// above alone. WebRTC solves this, and specifically @libp2p/webrtc's
// "private-to-private" transport (the `webRTC()` import below, distinct
// from `webRTCDirect` which is for dialing a peer with a known public
// address — not our case) — confirmed by reading its actual source, not
// assumed: it signals entirely through an EXISTING circuit-relay
// connection (SDP offer/answer and ICE candidates exchanged as an
// ordinary libp2p stream, Noise-encrypted like everything else), then
// upgrades to a direct WebRTC data-channel connection once negotiation
// completes — after which the relay plays no further part. Our
// bootstrap peer's circuit relay (already built for the browser <->
// Node case) turns out to already be exactly the signaling
// infrastructure this needs; no separate signaling protocol required.
//
// For a browser peer to be *reachable* this way (not just able to
// initiate), it needs to make a reservation with the relay — that's
// what the `/p2p-circuit` + `/webrtc` listen addresses below are: not a
// real listening socket (browsers still can't open one), but a
// reservation that lets the relay forward signaling on this peer's
// behalf when someone else wants to reach it.

import { createLibp2p } from 'libp2p'
import { webSockets } from '@libp2p/websockets'
import { webRTC } from '@libp2p/webrtc'
import { noise } from '@chainsafe/libp2p-noise'
import { yamux } from '@chainsafe/libp2p-yamux'
import { bootstrap } from '@libp2p/bootstrap'
import { identify } from '@libp2p/identify'
import { kadDHT } from '@libp2p/kad-dht'
import { circuitRelayTransport } from '@libp2p/circuit-relay-v2'
import { ping } from '@libp2p/ping'

/**
 * @param {object} [opts]
 * @param {string[]} [opts.bootstrapPeers] - multiaddrs of known bootstrap
 *   peers (ws:// addresses specifically — a browser can't dial tcp://).
 *   Without at least one, this browser peer has no way to discover or
 *   reach anyone at all — including via WebRTC, which also needs a
 *   relay connection to signal through.
 * @param {object} [opts.dhtOptions] - passed through to kadDHT(), merged
 *   with (not overriding) the forced clientMode below. See node.js's
 *   createNode() for why this matters for local/loopback testing
 *   specifically.
 * @param {boolean} [opts.allowInsecureLocalTesting] - default false.
 *   libp2p's own default browser connection gater refuses to dial (1)
 *   private/loopback addresses and (2) insecure ws:// (as opposed to
 *   wss://) — both deliberate, correct security defaults for a real
 *   deployment. A REAL deployment's fix is serving the bootstrap peer
 *   over wss:// (TLS) via a reverse proxy (e.g. Caddy), not weakening
 *   this gate — never set this true in production. It exists ONLY so
 *   local development/testing (a loopback bootstrap peer serving plain
 *   ws://) isn't blocked before you have TLS set up.
 * @param {object} [opts.privateKey] - libp2p private key for a stable peer ID
 *   (see node-key.js); omit for a random one-off ID.
 * @param {object} [opts.rtcConfiguration] - passed through to webRTC()'s
 *   RTCConfiguration (iceServers, etc). Without this, WebRTC connections
 *   only have STUN — which discovers your public address but cannot
 *   help when a router doesn't support NAT hairpinning (two devices on
 *   the same LAN connecting via their shared public IP) or when a real
 *   NAT genuinely blocks direct connectivity. A TURN server (included
 *   here as an iceServers entry with credentials, same object shape as
 *   the browser's native RTCPeerConnection expects) provides a relay
 *   fallback for exactly those cases — confirmed necessary directly:
 *   this project's own local testing hit a real hairpin-NAT failure
 *   (STUN-discovered candidates exchanged correctly, connectivity
 *   checks sent repeatedly, zero responses, eventual "failed" state)
 *   that only a TURN relay resolves.
 */
export async function createBrowserNode ({ bootstrapPeers = [], dhtOptions = {}, allowInsecureLocalTesting = false, rtcConfiguration = null, privateKey = null } = {}) {
  const usingBootstrap = bootstrapPeers.length > 0

  const peerDiscovery = []
  if (usingBootstrap) {
    peerDiscovery.push(bootstrap({ list: bootstrapPeers }))
  }

  const transports = [webSockets()]
  if (usingBootstrap) {
    transports.push(circuitRelayTransport())
    transports.push(webRTC(rtcConfiguration ? { rtcConfiguration } : {}))
  }

  const services = { identify: identify() }
  if (usingBootstrap) {
    services.ping = ping()
    // clientMode defaults to true (a browser node can never legitimately
    // serve as a DHT routing node — nothing can dial it to query it) but
    // dhtOptions can override this for testing: a tiny local-loopback DHT
    // (a handful of nodes total) may need every participant contributing
    // to the routing table for lookups to succeed at all — see
    // tests/dht-discovery-test.mjs and tests/browser-client-e2e-test.mjs,
    // both of which force server mode on every node for exactly this
    // reason, not just address filtering.
    services.dht = kadDHT({ clientMode: true, ...dhtOptions })
  }

  const node = await createLibp2p({
    // A supplied key gives this node a stable peer ID (see node-key.js); without
    // one libp2p generates a random key, i.e. a new peer ID on every start.
    ...(privateKey ? { privateKey } : {}),
    addresses: {
      // '/p2p-circuit' + '/webrtc': a reservation with the relay, not a
      // real listening socket — see module comment. Only meaningful
      // once a bootstrap/relay connection exists at all.
      listen: usingBootstrap ? ['/p2p-circuit', '/webrtc'] : []
    },
    transports,
    connectionEncrypters: [noise()],
    streamMuxers: [yamux()],
    peerDiscovery,
    services,
    connectionGater: allowInsecureLocalTesting ? { denyDialMultiaddr: () => false } : undefined
  })

  return node
}
